// src/components/Sidebar.tsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../api/axios';
import styles from './Sidebar.module.css';
import Tooltip from './Tooltip';
import { useLeaderView } from '../context/LeaderViewContext';
import { Calendar, User, Users, Home, Shield, Folder, ScrollText, ShieldCheck, Siren, Crown, FileText, Briefcase, Building2, Mail, Rocket, Trophy, Award, ClipboardCheck, MessageSquare, Plane, Menu, X, Wrench, Construction, ChevronLeft, ChevronRight, Sparkles, Ship, ChevronUp, ChevronDown, Hammer, Flag } from 'lucide-react';
import logo from '../assets/Icons/NEWTFICLOGO_no_bg.png';

const Sidebar: React.FC = () => {
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [pendingRequestsCount, setPendingRequestsCount] = useState<number>(0);
  const [myMissionsCount, setMyMissionsCount] = useState<number>(0);
  const [openMissionsCount, setOpenMissionsCount] = useState<number>(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState<number>(0);
  const [canAcceptMissions, setCanAcceptMissions] = useState<boolean>(false);
  const [pendingVerificationCount, setPendingVerificationCount] = useState<number>(0);
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
  const [showScrollBottom, setShowScrollBottom] = useState<boolean>(false);
  const sidebarRef = React.useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    // Load collapsed state from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    const isCollapsed = saved === 'true';
    // Set initial body attribute
    if (typeof document !== 'undefined') {
      document.body.setAttribute('data-sidebar-collapsed', String(isCollapsed));
    }
    return isCollapsed;
  });
  const { user } = useAuth();
  const { setActiveView } = useLeaderView();
  const location = useLocation();

  // Save collapsed state to localStorage and update body attribute
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(collapsed));
    // Set data attribute on body for CSS selectors
    document.body.setAttribute('data-sidebar-collapsed', String(collapsed));
  }, [collapsed]);

  // Close mobile sidebar when route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMobileOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle scroll to show/hide scroll indicators
  const handleScroll = () => {
    if (sidebarRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = sidebarRef.current;
      setShowScrollTop(scrollTop > 20);
      setShowScrollBottom(scrollTop < scrollHeight - clientHeight - 20);
    }
  };

  // Check scroll position on mount and when content changes
  useEffect(() => {
    const checkScroll = () => {
      handleScroll();
    };
    
    checkScroll();
    
    const sidebar = sidebarRef.current;
    if (sidebar) {
      sidebar.addEventListener('scroll', handleScroll);
      return () => {
        sidebar.removeEventListener('scroll', handleScroll);
      };
    }
  }, [openSection, collapsed, mobileOpen]);

  // Also check on window resize
  useEffect(() => {
    handleScroll();
  }, [openSection]);

  // Scroll functions
  const scrollUp = () => {
    if (sidebarRef.current) {
      sidebarRef.current.scrollBy({ top: -200, behavior: 'smooth' });
    }
  };

  const scrollDown = () => {
    if (sidebarRef.current) {
      sidebarRef.current.scrollBy({ top: 200, behavior: 'smooth' });
    }
  };

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  // Get user's department from their position
  const getUserDepartment = (): string | null => {
    if (!user) return null;
    
    // Check primary position first
    if (user.primaryPosition) {
      return user.primaryPosition.toLowerCase();
    }
    
    // Fall back to secondary
    if (user.secondaryPosition) {
      return user.secondaryPosition.toLowerCase();
    }
    
    // Fall back to tertiary
    if (user.tertiaryPosition) {
      return user.tertiaryPosition.toLowerCase();
    }
    
    return null;
  };

  // Check if user is CEO or COO
  const isCeoOrCoo = (): boolean => {
    if (!user) return false;
    const titles = [
      user.primaryPositionTitle,
      user.secondaryPositionTitle,
      user.tertiaryPositionTitle
    ].filter(Boolean).map((t: string) => t.toLowerCase());
    
    return titles.includes('ceo') || titles.includes('chief executive officer') || 
           titles.includes('coo') || titles.includes('chief operating officer');
  };

  // Check if user is C-Suite (Tier 4) - has leadership access
  const isCSuite = (): boolean => {
    if (!user) return false;
    return user.positionTier === 4;
  };

  // Every badge number in one request, on one timer.
  //
  // This was five independent 30-second timers hitting five endpoints: 10 requests
  // a minute from every open tab, on every page, for as long as it stayed open.
  // Two of them (/missions/board and /missions/my) downloaded the whole mission
  // list only to call .length on it, and a sixth call to /auth/user/profile ran on
  // mount purely to read orgPoints. /api/sidebar/counts answers all of it with
  // COUNT queries and returns canAcceptMissions alongside.
  //
  // It also stops while the tab is hidden and catches up on return, so a tab left
  // open in the background costs nothing instead of ~600 requests an hour.
  const fetchCounts = React.useCallback(async () => {
    try {
      const res = await axios.get('/sidebar/counts');
      const d = res.data ?? {};
      // null means "not entitled to this number", which is not the same as 0.
      setPendingRequestsCount(d.pendingRequests ?? 0);
      setPendingVerificationCount(d.pendingVerification ?? 0);
      setOpenMissionsCount(d.openMissions ?? 0);
      setMyMissionsCount(d.myMissions ?? 0);
      setUnreadMessagesCount(d.unreadMessages ?? 0);
      setCanAcceptMissions(Boolean(d.canAcceptMissions));
    } catch (err) {
      console.error('Failed to fetch sidebar counts:', err);
    }
  }, []);

  useEffect(() => {
    if (!user) return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const start = () => {
      stop();
      timer = setInterval(fetchCounts, 60000);
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else {
        fetchCounts();
        start();
      }
    };

    if (!document.hidden) {
      fetchCounts();
      start();
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user, fetchCounts]);

  return (
    <>
      {/* Mobile hamburger button */}
      <button 
        className={styles.mobileMenuBtn}
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
      >
        {mobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>
      
      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className={styles.mobileOverlay} 
          onClick={() => setMobileOpen(false)} 
        />
      )}
      
      <aside className={`${styles.sidebar} ${mobileOpen ? styles.mobileOpen : ''} ${collapsed ? styles.collapsed : ''}`}>
      
      {/* Scroll Up Indicator - Fixed overlay */}
      {showScrollTop && !collapsed && (
        <div 
          className={styles.scrollIndicatorTop}
          onClick={scrollUp}
        >
          <ChevronUp size={20} />
        </div>
      )}
      
      {/* Scrollable content wrapper */}
      <div ref={sidebarRef} className={styles.sidebarContent}>
      
      <div className={styles.sidebarLogo}>
        <img src={logo} alt="TFIC Logo" className={styles.logoImage} />
        <div className={styles.acronym}>TFIC</div>
      </div>
      <ul className={styles.navList}>

        {/* GENERAL */}
        <li className={styles.navSectionTitle} onClick={() => toggleSection('general')} style={{ cursor: 'pointer' }}>
          <span>{openSection === 'general' ? '▾' : '▸'}</span><span className={styles.sectionText}> General</span>
        </li>
        {(collapsed || openSection === 'general') && (
        <>
        <li className={styles.navItem}>
          <Link to="/home" className={styles.navLink} title="Home">
            <Home className={styles.icon} />
            <span className={styles.linkText}>Home</span>
          </Link>
        </li>
        <li className={styles.navItem}>
          <Link to="/aboutus" className={styles.navLink} title="About Us">
            <Users className={styles.icon} />
            <span className={styles.linkText}>About Us</span>
          </Link>
        </li>
        <li className={styles.navItem}>
          <Link to="/knowledge" className={styles.navLink} title="Knowledge Base">
            <ScrollText className={styles.icon} />
            <span className={styles.linkText}>Knowledge Base</span>
          </Link>
        </li>
        <li className={styles.navItem}>
          <Link to="/general/org-points" className={styles.navLink} title="ORG Points & Levels">
            <ShieldCheck className={styles.icon} />
            <span className={styles.linkText}>ORG Points & Levels</span>
          </Link>
        </li>
        <li className={styles.navItem}>
          <Link to="/chat" className={styles.navLink} title="Discord General Chat">
            <MessageSquare className={styles.icon} />
            <span className={styles.linkText}>Discord General Chat</span>
          </Link>
        </li>
        {user?.flags?.includes('OrgMember') && (
          <>
          <li className={styles.navItem}>
            <Link to="/events" className={styles.navLink} title="Events">
              <Calendar className={styles.icon} />
              <span className={styles.linkText}>Events</span>
            </Link>
          </li>
          <li className={styles.navItem}>
            <Link to="/members" className={styles.navLink} title="Members">
              <Users className={styles.icon} />
              <span className={styles.linkText}>Members</span>
            </Link>
          </li>
          <li className={styles.navItem}>
            <Link to="/recognition" className={styles.navLink} title="Recognition">
              <Trophy className={styles.icon} />
              <span className={styles.linkText}>Recognition</span>
            </Link>
          </li>
          </>
        )}
        </>
        )}

        {/* Divider before user sections */}
        <hr className={styles.sectionDivider} />

        {/* MY ACCOUNT */}
        {user && (
          <div className={styles.userSection}>
            <li className={styles.navSectionTitle} onClick={() => toggleSection('account')} style={{ cursor: 'pointer' }}>
              <span>{openSection === 'account' ? '▾' : '▸'}</span><span className={styles.sectionText}> My Account</span>
            </li>
            {(collapsed || openSection === 'account') && (
            <>
            <li className={styles.navItem}>
              <Tooltip 
                content="View your profile, ORG points, missions, and personalized recommendations"
                position="right"
              >
                <Link to="/dashboard" className={styles.navLink} title="My Profile">
                  <User className={styles.icon} />
                  <span className={styles.linkText}>My Profile</span>
                </Link>
              </Tooltip>
            </li>
            {user?.flags?.includes('OrgMember') && (
              <li className={styles.navItem}>
                <Tooltip 
                  content="Browse and accept available missions to earn ORG points and contribute to the organization"
                  position="right"
                >
                  <Link 
                    to="/missions" 
                    className={`${styles.navLink} ${openMissionsCount > 0 ? styles.pendingIndicator : ''}`}
                    title="Missions Board"
                  >
                    <ScrollText className={styles.icon} />
                    <span className={styles.linkText}>Missions Board</span>
                    {openMissionsCount > 0 && (
                      <span className={styles.countNumber}>({openMissionsCount})</span>
                    )}
                  </Link>
                </Tooltip>
              </li>
            )}
            {canAcceptMissions && (
              <li className={styles.navItem}>
                <Tooltip 
                  content="Track missions you've accepted and their completion status"
                  position="right"
                >
                  <Link 
                    to="/my-missions" 
                    className={`${styles.navLink} ${myMissionsCount > 0 ? styles.pendingIndicator : ''}`}
                    title="My Missions"
                  >
                    <ScrollText className={styles.icon} />
                    <span className={styles.linkText}>My Missions</span>
                    {myMissionsCount > 0 && (
                      <span className={styles.countNumber}>({myMissionsCount})</span>
                    )}
                  </Link>
                </Tooltip>
              </li>
            )}
            <li className={styles.navItem}>
              <Tooltip 
                content="View your submitted feedback and admin responses"
                position="right"
              >
                <Link to="/my-feedback" className={styles.navLink}>
                  <MessageSquare className={styles.icon} />
                  <span className={styles.linkText}>My Feedback</span>
                </Link>
              </Tooltip>
            </li>
            <li className={styles.navItem}>
              <Tooltip
                content="Manage your personal Star Citizen ship fleet"
                position="right"
              >
                <Link to="/my-fleet" className={styles.navLink}>
                  <Ship className={styles.icon} />
                  <span className={styles.linkText}>My Fleet <Construction size={16} color="#f0a000" style={{ marginLeft: '6px' }} /></span>
                </Link>
              </Tooltip>
            </li>
            <li className={styles.navItem}>
              <Tooltip
                content="See ships members have pledged to the org"
                position="right"
              >
                <Link to="/org-fleet" className={styles.navLink}>
                  <Flag className={styles.icon} />
                  <span className={styles.linkText}>Org Fleet</span>
                </Link>
              </Tooltip>
            </li>
            </>
            )}
          </div>
        )}

        {/* IN-GAME SUPPORT */}
        {user?.flags?.includes('OrgMember') && (
          <div className={styles.userSection}>
            <li className={styles.navSectionTitle} onClick={() => toggleSection('assistance')} style={{ cursor: 'pointer' }}>
              <span>{openSection === 'assistance' ? '▾' : '▸'}</span> In-Game Support
            </li>
            {(collapsed || openSection === 'assistance') && (
            <>
            <li className={styles.navItem}>
              <Link to="/medical-sos" className={styles.navLink}>
                <Siren className={styles.icon} />
                <span className={styles.linkText}>Medical SOS</span>
              </Link>
            </li>
            </>
            )}
          </div>
        )}

        {/* DEPARTMENTS */}
        {user && (
          <div className={styles.userSection}>
            <li className={styles.navSectionTitle} onClick={() => toggleSection('departments')} style={{ cursor: 'pointer' }}>
              <span>{openSection === 'departments' ? '▾' : '▸'}</span> Departments
            </li>
            {(collapsed || openSection === 'departments') && (
            <>
            <li className={styles.navItem}>
              <Link to="/departments" className={styles.navLink}>
                <Folder className={styles.icon} />
                <span className={styles.linkText}>All Departments</span>
              </Link>
            </li>
            </>
            )}
          </div>
        )}

        {/* Divider before admin sections */}
        {user && (user.role === 'Leader' || user.role === 'Admin') && (
          <hr className={styles.sectionDivider} />
        )}

        {/* ORG MANAGEMENT */}
        {user && (user.role === 'Leader' || user.role === 'Admin') && (
          <div className={styles.adminSection}>
            <li className={`${styles.navSectionTitle} ${styles.managementSection}`} onClick={() => toggleSection('management')} style={{ cursor: 'pointer' }}>
              <span>{openSection === 'management' ? '▾' : '▸'}</span> ORG Management
            </li>
            {(collapsed || openSection === 'management') && (
            <>
            <li className={`${styles.navItem} ${styles.managementItem}`}>
              <Tooltip 
                content="Access leadership tools, approve requests, manage users, and oversee organization operations"
                position="right"
                maxWidth="280px"
              >
                <Link to="/leader-dashboard" className={styles.navLink}>
                  <Shield className={styles.icon} />
                  <span className={styles.linkText}>Leader Dashboard</span>
                </Link>
              </Tooltip>
            </li>

            {/* My Inbox: Tier 4 only */}
            {user.positionTier === 4 && (
              <li className={`${styles.navItem} ${styles.managementItem}`}>
                <Tooltip 
                  content="Communicate with other Tier 4 leadership members through private messages and threads"
                  position="right"
                  maxWidth="280px"
                >
                  <Link 
                    to="/leadership-inbox" 
                    className={`${styles.navLink} ${unreadMessagesCount > 0 ? styles.pendingIndicator : ''}`}
                  >
                    <Mail className={styles.icon} />
                    <span className={styles.linkText}>My Inbox</span>
                    {unreadMessagesCount > 0 && (
                      <span className={styles.countNumber}>({unreadMessagesCount})</span>
                    )}
                  </Link>
                </Tooltip>
              </li>
            )}
            
            {/* User Management: Admin + CEO + COO */}
            {(user.role === 'Admin' || 
              [user.primaryPositionTitle, user.secondaryPositionTitle, user.tertiaryPositionTitle]
                .filter(Boolean)
                .map((t: string) => t.toLowerCase())
                .some((t: string) => ['ceo', 'chief executive officer', 'coo', 'chief operating officer'].includes(t))
            ) && (
              <li className={`${styles.navItem} ${styles.managementItem}`}>
                <Link to="/user-management" className={styles.navLink}>
                  <Users className={styles.icon} />
                  <span className={styles.linkText}>User Management</span>
                </Link>
              </li>
            )}

            <li className={`${styles.navItem} ${styles.managementItem}`}>
              <Link
                to="/leader-dashboard/missions"
                className={`${styles.navLink} ${pendingVerificationCount > 0 ? styles.pendingIndicator : ''}`}
              >
                <ScrollText className={styles.icon} />
                <span className={styles.linkText}>Manage Missions</span>
                {pendingVerificationCount > 0 && (
                  <span className={styles.countNumber}>({pendingVerificationCount})</span>
                )}
              </Link>
            </li>

            {/* Org Fleet Mgmt: All Leaders — see ships members have pledged for org use,
                with owner identity and audit log. Member-facing equivalent (anonymized,
                grouped) lives under MY ACCOUNT > "Org Fleet". */}
            <li className={`${styles.navItem} ${styles.managementItem}`}>
              <Link to="/leader-dashboard/org-fleet" className={styles.navLink}>
                <Flag className={styles.icon} />
                <span className={styles.linkText}>Org Fleet Mgmt</span>
              </Link>
            </li>

            {/* Manage Positions: Admin + CEO */}
            {(user.role === 'Admin' || 
              [user.primaryPositionTitle, user.secondaryPositionTitle, user.tertiaryPositionTitle]
                .filter(Boolean)
                .map((t: string) => t.toLowerCase())
                .some((t: string) => ['ceo', 'chief executive officer'].includes(t))
            ) && (
              <li className={`${styles.navItem} ${styles.managementItem}`}>
                <Link to="/positions-management" className={styles.navLink}>
                  <Briefcase className={styles.icon} />
                  <span className={styles.linkText}>Manage Positions</span>
                </Link>
              </li>
            )}

            <li className={`${styles.navItem} ${styles.managementItem}`}>
              <Tooltip 
                content="Manage your department's operations, members, and departmental content"
                position="right"
              >
                <Link 
                  to={`/departments/${getUserDepartment() || 'command'}/manage`} 
                  className={styles.navLink}
                >
                  <Building2 className={styles.icon} />
                  <span className={styles.linkText}>Department Management</span>
                </Link>
              </Tooltip>
            </li>

            {/* Position Applications: Admin + CEO */}
            {(user.role === 'Admin' || 
              [user.primaryPositionTitle, user.secondaryPositionTitle, user.tertiaryPositionTitle]
                .filter(Boolean)
                .map((t: string) => t.toLowerCase())
                .some((t: string) => ['ceo', 'chief executive officer'].includes(t))
            ) && (
              <li className={`${styles.navItem} ${styles.managementItem}`}>
                <Link to="/position-applications" className={styles.navLink}>
                  <Briefcase className={styles.icon} />
                  <span className={styles.linkText}>Position Applications</span>
                </Link>
              </li>
            )}

            <li className={`${styles.navItem} ${styles.managementItem}`}>
              <Link to="/posts-management" className={styles.navLink}>
                <FileText className={styles.icon} />
                <span className={styles.linkText}>Posts Management</span>
              </Link>
            </li>

            <li className={`${styles.navItem} ${styles.managementItem}`}>
              <Tooltip 
                content="Manage Knowledge Base articles, create guides, and publish training content"
                position="right"
                maxWidth="280px"
              >
                <Link to="/knowledge/admin" className={styles.navLink}>
                  <ScrollText className={styles.icon} />
                  <span className={styles.linkText}>Knowledge Base Admin</span>
                </Link>
              </Tooltip>
            </li>

            <li className={`${styles.navItem} ${styles.managementItem}`}>
              <Tooltip 
                content="Award commendations to members for outstanding achievements and contributions"
                position="right"
                maxWidth="280px"
              >
                <Link to="/award-commendation" className={styles.navLink}>
                  <Award className={styles.icon} />
                  <span className={styles.linkText}>Award Commendation</span>
                </Link>
              </Tooltip>
            </li>

            <li className={`${styles.navItem} ${styles.managementItem}`}>
              <Tooltip 
                content="Review and respond to user feedback submissions"
                position="right"
                maxWidth="280px"
              >
                <Link to="/feedback-management" className={styles.navLink}>
                  <MessageSquare className={styles.icon} />
                  <span className={styles.linkText}>Feedback Management</span>
                </Link>
              </Tooltip>
            </li>

            {/* Metrics Dashboard: Admin only */}
            {user.role === 'Admin' && (
              <li className={`${styles.navItem} ${styles.managementItem}`}>
                <Link to="/metrics-dashboard" className={styles.navLink}>
                  <Users className={styles.icon} />
                  <span className={styles.linkText}>Metrics Dashboard</span>
                </Link>
              </li>
            )}

            {(user.role === 'Admin' || [4, 5, 11].includes(user.primaryPosition?.id)) && (
              <li className={`${styles.navItem} ${styles.managementItem}`}>
                <Tooltip 
                  content="Review and approve member requests for permissions, positions, and organization access"
                  position="right"
                  maxWidth="280px"
                >
                  <Link 
                    to="/leader-dashboard/permission-requests" 
                    className={`${styles.navLink} ${pendingRequestsCount > 0 ? styles.criticalIndicator : ''}`}
                  >
                    <ShieldCheck className={styles.icon} />
                    <span className={styles.linkText}>Approval Requests</span>
                    {pendingRequestsCount > 0 && (
                      <span className={styles.countNumber}>({pendingRequestsCount})</span>
                    )}
                  </Link>
                </Tooltip>
              </li>
            )}
            {user.positionTier === 4 && (
              <li className={`${styles.navItem} ${styles.managementItem}`}>
                <Link to="/leadership-guide" className={styles.navLink}>
                  <Crown className={styles.icon} />
                  <span className={styles.linkText}>Leadership Guide</span>
                </Link>
              </li>
            )}
            {(user.positionTier === 4 || user.flags?.includes('CanWriteAfterActionReports') || user.flags?.includes('CanManageEvents')) && (
              <li className={`${styles.navItem} ${styles.managementItem}`}>
                <Link to="/after-action-reports" className={styles.navLink}>
                  <ClipboardCheck className={styles.icon} />
                  <span className={styles.linkText}>After Action Reports</span>
                </Link>
              </li>
            )}
            </>
            )}
          </div>
        )}

        {/* COMMUNITY RESOURCES - Public Access */}
        <li className={styles.navSectionTitle} onClick={() => toggleSection('community')} style={{ cursor: 'pointer' }}>
          <span>{openSection === 'community' ? '▾' : '▸'}</span> Community Resources
        </li>
        {(collapsed || openSection === 'community') && (
        <>
        <li className={styles.navItem}>
          <Link to="/ship-advisor" className={styles.navLink}>
            <Sparkles className={styles.icon} />
            <span className={styles.linkText}>Ship Advisor</span>
          </Link>
        </li>
        <li className={styles.navItem}>
          <Link to="/ship-database" className={styles.navLink}>
            <Plane className={styles.icon} />
            <span className={styles.linkText}>Ship Database <Construction size={16} color="#f0a000" style={{ marginLeft: '6px' }} /></span>
          </Link>
        </li>
        <li className={styles.navItem}>
          <Link to="/loadout-builder" className={styles.navLink}>
            <Wrench className={styles.icon} />
            <span className={styles.linkText}>Loadout Builder <Construction size={16} color="#f0a000" style={{ marginLeft: '6px' }} /></span>
          </Link>
        </li>
        <li className={styles.navItem}>
          <Link to="/crafting-blueprints" className={styles.navLink}>
            <Hammer className={styles.icon} />
            <span className={styles.linkText}>Crafting Blueprints <Construction size={16} color="#f0a000" style={{ marginLeft: '6px' }} /></span>
          </Link>
        </li>
        </>
        )}

        {/* ORG TOOLS - Member features */}
        {user?.flags?.includes('OrgMember') && (
          <>
          <li className={styles.navSectionTitle} onClick={() => toggleSection('orgtools')} style={{ cursor: 'pointer' }}>
            <span>{openSection === 'orgtools' ? '▾' : '▸'}</span> ORG Tools
          </li>
          {(collapsed || openSection === 'orgtools') && (
            <>
            {/* Future org-only tools will go here */}
            </>
          )}
          </>
        )}

        {/* SITE INFO */}
        <li className={styles.navSectionTitle} onClick={() => toggleSection('siteinfo')} style={{ cursor: 'pointer' }}>
          <span>{openSection === 'siteinfo' ? '▾' : '▸'}</span> Site Info
        </li>
        {(collapsed || openSection === 'siteinfo') && (
        <>
        <li className={styles.navItem}>
          <Link to="/coming-soon" className={styles.navLink}>
            <Rocket className={styles.icon} />
            <span className={styles.linkText}>Coming Soon</span>
          </Link>
        </li>
        <li className={styles.navItem}>
          <Link to="/privacy-policy" className={styles.navLink}>
            <ScrollText className={styles.icon} />
            <span className={styles.linkText}>Privacy Policy</span>
          </Link>
        </li>
        <li className={styles.navItem}>
          <Link to="/terms-of-service" className={styles.navLink}>
            <ShieldCheck className={styles.icon} />
            <span className={styles.linkText}>Terms of Service</span>
          </Link>
          </li>
        </>
        )}
      </ul>
      
      </div>
      {/* End scrollable content wrapper */}
      
      {/* Scroll Down Indicator - Fixed overlay */}
      {showScrollBottom && !collapsed && (
        <div 
          className={styles.scrollIndicatorBottom}
          onClick={scrollDown}
        >
          <ChevronDown size={20} />
        </div>
      )}
      
      {/* Collapse toggle button at bottom */}
      <button 
        className={styles.collapseBtn}
        onClick={() => setCollapsed(!collapsed)}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        {!collapsed && <span className={styles.collapseText}>Collapse</span>}
      </button>
    </aside>
    </>
  );
};

export default Sidebar;
